import {
  AuctionBiddingEngine,
  AuctionBiddingStatus,
  AuctionProgressStatus,
  AuctionResponse,
  AuctionResponseDepartment,
  AuctionType,
  AuctionViewing,
  Brand,
  EAuctionBiddingEngine,
  FollowType,
  LotStatus,
  PersonResponse,
  SaleStatus,
  getAuctionProgressStatus,
} from '@bonhams1793/bonhams-typescript'
import getConfig from 'next/config'
import { Product } from 'schema-dts'

import { a3CurrencyConverter } from '@nx/a3'
import { LotResponse } from '@nx/api'
import { decodeHtml } from '@nx/decode-html'
import {
  FireAdditionalInformation,
  InfoType,
  useInformation,
} from '@nx/fire/additional-information'
import { GavelIcon, LoveIcon, LoveSelectedIcon } from '@nx/fire/assets'
import { FireBreadcrumbs, FireBreadcrumbsProps } from '@nx/fire/breadcrumbs'
import { FireFollowButton } from '@nx/fire/follow-button'
import { FireFollowCard } from '@nx/fire/follow-card'
import { FireLotNumberAndSymbols } from '@nx/fire/lot-number-and-symbols'
import { FireSection } from '@nx/fire/section'
import { ShareSheet } from '@nx/fire/share-sheet'
import { Body } from '@nx/fire/typography'
import {
  buildAuctionPath,
  buildLocationPath,
  extractDataFromHTML,
  replaceNewLinesWithLineBreaks,
  stripTags,
  useNewImageSubdomain,
} from '@nx/helpers'
import { SmartlingAttributes } from '@nx/smartling'
import { getTranslate } from '@nx/translations'
import {
  useLotAnalytics,
  useUserInteraction,
} from '@nx/utils/google-tag-manager'

import { useModals } from '@web/components/common/ModalManager/ModalContext'
import { LotsPromo } from '@web/components/lot/LotsPromo'
import { useGetLaunchDarklyFlags } from '@web/helpers/api/launchDarkly'
import { useGetSimilarLots } from '@web/helpers/api/similarLots/useGetSimilarLots'
import { useGetUser } from '@web/helpers/api/user'
import { populateUrl } from '@web/helpers/utils/urlBuilders/populateUrl'

import { Contacts } from '../../common/Contacts'
import { MetaData } from '../../common/MetaData'
import { NewsAndHighlights } from '../../common/NewsAndHighlights'
import { ActionPanel } from '../ActionPanel'
import { AMWidget } from '../AuctionMobility'
import { ConsignmentPromo } from '../ConsignmentPromo'
import { Description } from '../Description'
import { EndingFrom } from '../EndingFrom'
import { LightboxCarousel } from '../LightboxCarousel/LightboxCarousel'
import { LotPageBiddingWidget } from '../LotPageBiddingWidget/LotPageBiddingWidget'
import { SimilarLots } from '../LotsPromo/SimilarLots'
import { Navigation } from '../Navigation'
import { Price } from '../Price'
import { StartingFrom } from '../StartingFrom'
import {
  ContactsWrapper,
  FollowPromoCardWrapper,
  LotActions,
  LotBreadcrumbsContainer,
  LotCarouselContainer,
  LotContainer,
  LotDescriptionContainer,
  LotDetailsContainer,
  LotHeading,
  LotInfoContainer,
  LotNavigationContainer,
  StyledAuctionLinkContainer,
  StyledAuctionLinkLabel,
  StyledBanner,
  StyledBannerInner,
  StyledBannerLink,
  StyledHeading,
  StyledLocationLink,
  StyledLotStatusLabel,
  StyledLotTitle,
} from './LotPage.styles'
import translate from './LotPage.translate.json'
import { useAddRecentlyViewedLots } from './useAddRecentlyViewedLots'

const { publicRuntimeConfig } = getConfig()
const { text } = getTranslate(translate)

export function LotPage({
  auction,
  lot,
  contacts,
  auctionViewings,
}: LotPageProps) {
  const { recordInteraction } = useUserInteraction()
  const { dispatch } = useModals()
  const flags = useGetLaunchDarklyFlags()
  const { data: user } = useGetUser()
  const similarLots = useGetSimilarLots({
    lotDepartmentCode: lot.sale_info.sDepartment,
    enabled: !lot.isToBeSold,
  })

  const isAddressInRegistrationModalEnabled =
    !!flags.data?.['enable-address-in-registration-modal']

  const infoItems = useInformation({ auction, lot, auctionViewings })

  const auctionViewingsData = infoItems.find(
    (item) => item.id === InfoType.auctionViewing
  )

  useLotAnalytics({
    auction,
    lot,
    userIsLoggedIn: !!user,
  })

  useAddRecentlyViewedLots({ lotId: lot.iSaleLotNoUnique })

  const lotHeading = replaceNewLinesWithLineBreaks(
    extractDataFromHTML(lot.sCatalogDesc, 'LotHeading')
  )
  const lotName =
    stripTags(extractDataFromHTML(lot.sCatalogDesc, 'LotName')) || lot.sDesc
  const lotDepartment = auction.departments.find(
    (department: AuctionResponseDepartment) =>
      department.sDepartment === auction.sDepartment
  )
  const initialPrimaryImageUrl =
    lot.images.find((img) => img.lPrimary)?.image_url ||
    lot.images[0]?.image_url ||
    ''
  const auctionProgressStatus = getAuctionProgressStatus({
    status: auction.sSaleStatus,
    type: auction.sSaleType,
    biddingStatus: auction.sBidOnlineStatus,
    endDate: auction.dates.end.datetime,
    startDate: auction.dates.start[0].date.datetime,
    localTimezone: auction.dates.start[0].date.timezone.iana,
    consignmentEndDate: auction.dates.consignment?.datetime
      ? auction.dates.consignment.datetime
      : null,
    isCharity: Boolean(auction.lCharity),
  })

  let price = 0
  if (!lot.lNotPublishedResults) {
    price =
      lot.sLotStatus === LotStatus.sold ? lot.dHammerPremium : lot.dEstimateLow
  }

  const { addNewImageSubdomain } = useNewImageSubdomain()

  const seoImage = initialPrimaryImageUrl
    ? addNewImageSubdomain(initialPrimaryImageUrl)
    : populateUrl('/web-assets/bonhams-logo.png')

  const ldJson: Product = {
    '@type': 'Product',
    name: lotName,
    image: seoImage,
    description: stripTags(extractDataFromHTML(lot.sCatalogDesc, 'LotDesc')),
    offers: {
      '@type': 'Offer',
      priceCurrency: a3CurrencyConverter(lot.currency.iso_code),
      price,
      availability:
        lot.sLotStatus === LotStatus.new
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
    },
  }

  const breadcrumbs: FireBreadcrumbsProps['linksStructure'] = [
    {
      title: decodeHtml(
        stripTags(extractDataFromHTML(auction.sSaleNameStyled, 'firstLine'))
      ),
      url: buildAuctionPath({
        auctionId: auction.iSaleNo,
        biddingEngine: lot.biddingEngine,
        slug: auction.slug,
      }),
      translate: true,
    },
    {
      title: decodeHtml(lotName),
      translate: false,
    },
  ]

  const isAuctionEnded =
    auction.sSaleStatus === SaleStatus.finished ||
    (auction.sSaleStatus === SaleStatus.ready &&
      auction.sBidOnlineStatus === AuctionBiddingStatus.ended)

  return (
    <>
      <MetaData
        name={stripTags(lot.sDesc)}
        brand={auction.brand as Brand}
        description={stripTags(
          extractDataFromHTML(lot.sCatalogDesc, 'LotDesc')
        )}
        imageUrl={
          initialPrimaryImageUrl
            ? populateUrl(initialPrimaryImageUrl, { width: 580 })
            : ''
        }
        ldJson={ldJson}
        shouldBeTranslated={false}
        baseUrl={publicRuntimeConfig.WEB_BASE_URL}
      />

      {flags.data?.['similar-lots-finished-lot'] &&
      !lot.isToBeSold &&
      similarLots.data.length ? (
        <>
          <StyledBanner>
            <FireSection verticalSpacing="none">
              <StyledBannerInner>
                <GavelIcon />
                <Body>
                  {text(
                    isAuctionEnded
                      ? 'banner.auctionEnded'
                      : 'banner.lotUnavailable'
                  )}{' '}
                  <StyledBannerLink shallow={true} href="#lot-details">
                    {text('bannerCTA')}
                  </StyledBannerLink>
                </Body>
              </StyledBannerInner>
            </FireSection>
          </StyledBanner>
          <FireSection background="white" verticalSpacing="none">
            <SimilarLots
              key={lot.iSaleLotNoUnique}
              isFetching={similarLots.isFetching}
              similarLots={similarLots.data}
              variant="carousel"
            />
          </FireSection>
        </>
      ) : null}

      <FireSection id="lot-details">
        <LotContainer>
          <LotDetailsContainer>
            <LotBreadcrumbsContainer>
              <FireBreadcrumbs linksStructure={breadcrumbs} />
            </LotBreadcrumbsContainer>

            <LotNavigationContainer>
              <Navigation lot={lot} auctionSaleNumber={auction.iSaleNo} />
            </LotNavigationContainer>

            <LotCarouselContainer
              data-testid="lot-carousel"
              {...SmartlingAttributes.noTranslate}
            >
              <LightboxCarousel
                key={`carousel-${lot.iSaleItemNo}`}
                images={lot.images}
                videos={lot.video_ids}
                description={lot.sDesc}
              />
            </LotCarouselContainer>

            <LotInfoContainer>
              {lotHeading && (
                <LotHeading
                  as="div"
                  $level="S"
                  dangerouslySetInnerHTML={{ __html: lotHeading }}
                  {...SmartlingAttributes.noTranslate}
                />
              )}
              <FireLotNumberAndSymbols
                lExhibition={lot.lExhibition}
                iSaleLotNo={lot.iSaleLotNo}
                sSaleLotNoA={lot.sSaleLotNoA}
                lotSymbols={lot.lot_symbols.map((s) => ({
                  symbol: s.sLotSymbol,
                  longDescription: s.sLongDesc,
                  shortDescription: s.sShortDesc,
                }))}
              />
              <StyledLotTitle
                {...SmartlingAttributes.noTranslate}
                as="h1"
                dangerouslySetInnerHTML={{ __html: lot.sStyledDesc }}
              />

              {lot.sLotStatus === LotStatus.withdrawn && (
                <StyledLotStatusLabel>
                  {text('lotStatus.withdrawn')}
                </StyledLotStatusLabel>
              )}
              {lot.lAddendum && (
                <StyledLotStatusLabel>
                  {text('lotStatus.amended')}
                </StyledLotStatusLabel>
              )}

              <StyledAuctionLinkContainer>
                <StyledAuctionLinkLabel
                  href={buildAuctionPath({
                    auctionId: auction.iSaleNo,
                    biddingEngine: lot.biddingEngine,
                    slug: auction.slug,
                  })}
                  onClick={() =>
                    recordInteraction({
                      category: 'Lot Page - Auction Details',
                      action: 'auction-link.click',
                      label: `${auction.iSaleNo}`,
                    })
                  }
                >
                  {auction.sSaleName}
                </StyledAuctionLinkLabel>
              </StyledAuctionLinkContainer>

              {lot.iSaleNo.sSaleType === AuctionType.online ? (
                <EndingFrom auction={auction} />
              ) : lot.iSaleNo.sSaleType === AuctionType.live ? (
                <StartingFrom hammerTime={lot.hammerTime} />
              ) : null}

              <StyledLocationLink
                href={buildLocationPath(
                  auction.sDivision,
                  auction.locations[0]?.sDivisionName
                )}
              >
                {auction.sVenue}
              </StyledLocationLink>

              <Price
                auctionType={auction.sSaleType}
                lotStatus={lot.sLotStatus}
                lNotPublishedResults={lot.lNotPublishedResults}
                dHammerPrice={lot.dHammerPrice}
                dHammerPremium={lot.dHammerPremium}
                lExhibition={lot.lExhibition}
                lReferDept={lot.lReferDept}
                lWithoutReserve={lot.lWithoutReserve}
                dEstimateLow={lot.dEstimateLow}
                sCurrencySymbol={lot.sCurrencySymbol}
                dEstimateHigh={lot.dEstimateHigh}
                currencyISO={lot.currency?.iso_code}
                GBPLowEstimate={lot.dLotLowValue}
                GBPHighEstimate={lot.dLotHighValue}
                showSoldPrice={lot.biddingEngine !== AuctionBiddingEngine.BBE}
              />

              {lot.biddingEngine === AuctionBiddingEngine.AM ? (
                <AMWidget
                  key={`am-${lot.rowid}-${!!user}`}
                  auction={auction}
                  lot={lot}
                  options={{
                    showCountdown: auction.sSaleType === AuctionType.online,
                    isUserLoggedIn: !!user,
                    hasHammerPrice: !!lot.dHammerPrice,
                  }}
                />
              ) : lot.biddingEngine === AuctionBiddingEngine.BBE ? (
                <LotPageBiddingWidget
                  auction={{
                    auctionId: String(auction.iSaleNo),
                    auctionStatus: auction.sSaleStatus,
                    auctionType: auction.sSaleType,
                    biddingStatus: auction.sBidOnlineStatus,
                    startDate: {
                      datetime: auction.dates.start[0].date.datetime,
                      timestamp: 0,
                      timezone: auction.dates.start[0].date.timezone,
                    },
                  }}
                  lot={{
                    biddingEngine: lot.biddingEngine,
                    brand: lot.brand,
                    currencySymbol: lot.sCurrencySymbol,
                    hammerPremium: lot.dHammerPremium,
                    hammerPrice: lot.dHammerPrice,
                    hammerTime: {
                      datetime: lot.hammerTime.datetime,
                      timestamp: 0,
                      timezone: lot.hammerTime.timezone,
                    },
                    isExhibition: lot.lExhibition,
                    isPreviewLot: lot.lPreviewLot,
                    isResultPublished: lot.isResultPublished,
                    isToBeSold: lot.isToBeSold,
                    lotNo: `${lot.iSaleLotNo}${lot.sSaleLotNoA}`,
                    lotUniqueId: String(lot.iSaleLotNoUnique),
                    status: lot.sLotStatus,
                  }}
                  isAddressInRegistrationModalEnabled={
                    isAddressInRegistrationModalEnabled
                  }
                />
              ) : null}

              <LotActions>
                <FireFollowButton
                  itemId={lot.iSaleItemNo}
                  type={FollowType.lot}
                  metadata={{
                    auctionId: auction.iSaleNo,
                  }}
                  baseURL={publicRuntimeConfig.WEB_API_URL}
                  followIcons={[LoveSelectedIcon, LoveIcon]}
                  openLogin={() => {
                    dispatch?.({
                      type: 'OPEN',
                      payload: {
                        modal: 'LOGIN',
                        props: {
                          brand: auction.brand,
                          user: null,
                        },
                      },
                    })
                  }}
                />
                <ShareSheet
                  title={lotName}
                  type={text('socialShare.type')}
                  url={publicRuntimeConfig.WEB_BASE_URL}
                />
              </LotActions>

              <ActionPanel
                auction={auction}
                lot={lot}
                contacts={contacts}
                auctionViewingsData={auctionViewingsData}
              />

              {[
                LotStatus.sold,
                LotStatus.unsold,
                LotStatus.withoutResults,
              ].includes(lot.sLotStatus as LotStatus) && (
                <ConsignmentPromo
                  departmentCode={lotDepartment?.sDepartment}
                  departmentName={lotDepartment?.sDepartmentName}
                />
              )}

              {[
                AuctionProgressStatus.comingSoon,
                AuctionProgressStatus.previewReady,
              ].includes(auctionProgressStatus) && (
                <FollowPromoCardWrapper>
                  <FireFollowCard
                    itemId={lot.iSaleItemNo}
                    type={FollowType.lot}
                    metadata={{ auctionId: auction.iSaleNo }}
                    baseURL={publicRuntimeConfig.WEB_API_URL}
                    openLogin={() => {
                      dispatch?.({
                        type: 'OPEN',
                        payload: {
                          modal: 'LOGIN',
                          props: {
                            brand: auction.brand,
                            user: null,
                          },
                        },
                      })
                    }}
                  />
                </FollowPromoCardWrapper>
              )}

              {!!contacts?.length && (
                <ContactsWrapper {...SmartlingAttributes.noTranslate}>
                  <StyledHeading $level={5} as="h2">
                    {text('specialists.title')}
                  </StyledHeading>
                  <Contacts
                    contacts={contacts}
                    auction={auction}
                    lot={lot}
                    isFullWidth
                  />
                </ContactsWrapper>
              )}
            </LotInfoContainer>

            <LotDescriptionContainer>
              <Description lot={lot} />
              <FireAdditionalInformation items={infoItems} />
            </LotDescriptionContainer>
          </LotDetailsContainer>
        </LotContainer>
      </FireSection>

      {!!lot.related_press?.length && (
        <FireSection>
          <NewsAndHighlights
            relatedPress={lot.related_press}
            trackingCategory="Lot Page - News and Stories"
          />
        </FireSection>
      )}

      <LotsPromo
        auctionId={lot.iSaleNo.iSaleNo}
        lotStatus={lot.sLotStatus}
        lotNumber={lot.iSaleLotNo}
        lotUniqueId={lot.iSaleLotNoUnique}
        lotDepartmentCode={lot.sale_info.sDepartment}
      />
    </>
  )
}

export interface LotPageProps {
  auction: AuctionResponse
  lot: LotResponse & {
    biddingEngine: EAuctionBiddingEngine | null
    isResultPublished: boolean
    isToBeSold: boolean
  }
  contacts: PersonResponse[]
  auctionViewings: AuctionViewing[]
}
